import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import QrScanner from 'qr-scanner';
import { Camera, CameraOff, X } from 'lucide-react';

interface QRCodeScannerProps {
  onCodeScanned: (code: string) => void;
  onClose: () => void;
  isOpen: boolean;
}

export const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onCodeScanned,
  onClose,
  isOpen
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [scanner, setScanner] = useState<QrScanner | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      startScanner();
    }

    return () => {
      if (scanner) {
        scanner.destroy();
      }
    };
  }, [isOpen]);

  const startScanner = async () => {
    if (!videoRef.current) return;

    try {
      setError(null);
      setIsScanning(true);

      // Mobile-specific camera configuration
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      const qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          try {
            // Try to parse as JSON first (for QR codes from our system)
            const parsed = JSON.parse(result.data);
            if (parsed.type === 'team_access_code' && parsed.code) {
              onCodeScanned(parsed.code);
              return;
            }
          } catch {
            // If not JSON, treat as plain text code
            if (result.data && result.data.length === 8) {
              onCodeScanned(result.data);
            } else {
              setError('Invalid QR code format');
            }
          }
        },
        {
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: isMobile ? 'environment' : 'user', // Use back camera on mobile
          maxScansPerSecond: 2, // Reduce for better performance on mobile
          returnDetailedScanResult: true
        }
      );

      // Set video constraints for better mobile support
      qrScanner.setCamera(isMobile ? 'environment' : 'user');
      
      await qrScanner.start();
      setScanner(qrScanner);
    } catch (err: any) {
      console.error('Error starting QR scanner:', err);
      setError('Failed to start camera. Please check permissions and try again.');
      setIsScanning(false);
    }
  };

  const stopScanner = () => {
    if (scanner) {
      scanner.stop();
      scanner.destroy();
      setScanner(null);
    }
    setIsScanning(false);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Scan QR Code
            </CardTitle>
            <CardDescription>
              Point your camera at the team access QR code
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <video
              ref={videoRef}
              className="w-full h-64 bg-black rounded-lg object-cover"
              style={{ display: isScanning ? 'block' : 'none' }}
            />
            {!isScanning && (
              <div className="w-full h-64 bg-muted rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <CameraOff className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Camera not started
                  </p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="flex gap-2">
            {!isScanning ? (
              <Button onClick={startScanner} className="flex-1">
                <Camera className="h-4 w-4 mr-2" />
                Start Camera
              </Button>
            ) : (
              <Button onClick={stopScanner} variant="outline" className="flex-1">
                <CameraOff className="h-4 w-4 mr-2" />
                Stop Camera
              </Button>
            )}
            <Button onClick={handleClose} variant="outline">
              Cancel
            </Button>
          </div>

          <div className="text-xs text-muted-foreground text-center">
            Make sure to allow camera permissions when prompted
          </div>
        </CardContent>
      </Card>
    </div>
  );
};